import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core'
import { CommonModule } from '@angular/common'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { ButtonComponent } from '../button/button.component'
import { RadioComponent } from '../radio/radio.component'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { GalleryImage } from '../../interfaces/api/GalleryImage'
import { ComboboxComponent, Option } from '@components/combobox/combobox.component'
import { ApiService } from '../../services/api.service'
import { LabelValuePair } from '../../interfaces/api/LabelValuePair'
import { SelectComponent } from '@components/select/select.component'
import { SelectValue } from '../../interfaces/select-value.interface'

@Component({
  selector: 'app-gallery-photos',
  standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslateModule, RadioComponent, ButtonComponent, ComboboxComponent, SelectComponent],
  templateUrl: './gallery-photos.component.html',
  styleUrl: './gallery-photos.component.scss'
})
export class GalleryPhotosComponent implements OnInit {
  applications : string[];
  brands : string[];
  colors : LabelValuePair<string>[];
  colorsAll : LabelValuePair<string>[];
  @Input() images: GalleryImage[] = [];

  errorMessages: string[] = [];

  @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;
  @Output() imagesUploaded = new EventEmitter<GalleryImage[]>();
  @Output() validateChanges = new EventEmitter<boolean>();
  @Input() creating = false;

    applicationOptions: SelectValue[] = []
    brandOptions: SelectValue[] = []
    colorOptions: SelectValue[] = []
    colorOptionsByIndex: SelectValue[][] = []

  constructor(private eRef: ElementRef, private api: ApiService, private translate: TranslateService) {}


  ngOnInit(): void {
      this.api.myGalleries__applications().subscribe({
          next: applications => {
              this.applications = applications
              this.applicationOptions = this.getApplications()
          },
          error: err => {
              console.error(err);
          }
      })

    this.api.myGalleries__brands().subscribe({
        next: brands => {
            this.brands = brands
            this.brandOptions = this.getBrands()
        },
        error: err => {
            console.error(err);
        }
    });
    this.api.myGalleries__colors().subscribe({
        next: colors => {
            this.colors = colors
            this.colorsAll = colors
            this.colorOptions = this.getColors()
            // Initialise per-image color options for any images already present (edit mode)
            for (let i = this.colorOptionsByIndex.length; i < this.images.length; i++) {
                this.colorOptionsByIndex.push(this.colorOptions)
            }
        },
        error: err => {
            console.error(err);
        }
    });
  }

  triggerFileInput(): void {
    this.fileInputRef.nativeElement.click();
  }

 async onFileSelected(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement;
  if (!input.files) return;

  this.errorMessages = [];

  const acceptedTypes = ['image/jpeg', 'image/png'];

  for (const file of Array.from(input.files)) {
    // Validar tipo de archivo
    if (!acceptedTypes.includes(file.type)) {
      const msg = this.translate.instant('new-galleries.invalidFormat', { filename: file.name });
      this.errorMessages.push(msg);
      continue;
    }

    // Validar tamaño
    const maxSizeMB = 2.5;
    if (file.size > maxSizeMB * 1024 * 1024) {
      const msg = this.translate.instant('new-galleries.fileTooLarge', { filename: file.name });
      this.errorMessages.push(msg);
      continue;
    }

    // Validar dimensiones
    const imageUrl = URL.createObjectURL(file);
    const image = new Image();

    const isValid = await new Promise<boolean>((resolve) => {
      image.onload = () => {
        const validResolution = image.width >= 1024 && image.height >= 768;
        if (!validResolution) {
          const msg = this.translate.instant('new-galleries.lowResolution', {
            filename: file.name,
            width: image.width,
            height: image.height,
          });
          this.errorMessages.push(msg);
        }
        resolve(validResolution);
      };
      image.onerror = () => {
        const msg = this.translate.instant('new-galleries.loadError', { filename: file.name });
        this.errorMessages.push(msg);
        resolve(false);
      };
      image.src = imageUrl;
    });

    if (!isValid) {
      URL.revokeObjectURL(imageUrl);
      continue;
    }

    const base64 = await this.api.fileToBase64(file);

    this.images.push({
      tempUrl: imageUrl,
      image: base64,
      filename: file.name,
      isCover: false,
      order: this.images.length,
      application: null,
      brand: null,
      color: null,
    });
    this.colorOptionsByIndex.push(this.colorOptions);
  }

  this.imagesUploaded.emit(this.images);
  input.value = '';
  this.validateChanges.emit(this.isValid());
 }



  deleteAllImages(): void {
    this.images = [];
    this.colorOptionsByIndex = [];
    this.imagesUploaded.emit(this.images);
    this.validateChanges.emit(this.isValid());
  }

  getApplications(): SelectValue[] {
      return this.applications.map(application => ({ id: application, value: application, label: "gallery-application." + application }));
  }

  getBrands(): SelectValue[] {
    return this.brands.map(application => ({ id: application, value: application, label: application.substring(0, 1).toUpperCase() + application.substring(1).toLowerCase() }));
  }

  getColors(): SelectValue[] {
    return this.colors.map(keyValuePair => ({ id: keyValuePair.value, value: keyValuePair.value, label: keyValuePair.label }));
  }

  selectApplication(index: number, application: SelectValue | null): void {
    this.images[index].application = application?.value;
    this.validateChanges.emit(this.isValid());
  }

  getSelectedApplication(index: number): SelectValue | null {
    const application = this.images[index].application;
    if (application) {
        return this.applicationOptions.find(applicationValue => applicationValue.value === application) || null;
    }
    return null;
  }

  selectBrand(index: number, brand: SelectValue | null): void {
    this.images[index].brand = brand?.value;
    this.images[index].color = null;
    this.validateChanges.emit(this.isValid());
    if (brand && brand.value) {
      this.searchColorByBrand(index, brand)
    } else {
      this.colorOptionsByIndex[index] = this.colorOptions
    }
  }

  private searchColorByBrand(index: number, brand: SelectValue) {
    this.api.myGalleries__colorsByBrand(brand?.value || null).subscribe({
      next: colors => {
        this.colorOptionsByIndex[index] = colors.map(kv => ({ id: kv.value, value: kv.value, label: kv.label }))
      },
      error: err => {
        console.error(err)
      }
    })
  }

  getColorOptionsForIndex(index: number): SelectValue[] {
    return this.colorOptionsByIndex[index] ?? this.colorOptions
  }

    getSelectedBrand(index: number): SelectValue | null {
        const brand = this.images[index].brand;
        if (brand) {
            return this.brandOptions.find(brandValue => brandValue.value === brand) || null;
        }
        return null;
    }

  selectColor(index: number, color: SelectValue | null): void {
    this.images[index].color = color?.value;
    this.validateChanges.emit(this.isValid());
  }

    getSelectedColor(index: number): SelectValue | null {
        const color = this.images[index].color;
        if (color) {
            const opts = this.getColorOptionsForIndex(index);
            return opts.find(c => c.value === color) || this.colorOptions.find(c => c.value === color) || null;
        }
        return null;
    }


  setAsMainImage(index: number): void {
    this.images.forEach((img, i) => {
      img.isCover = i === index;
    });
    this.validateChanges.emit(this.isValid());
  }

  applyToAll(index: number): void {
    const sourceImage = this.images[index];

    for (let i = 0; i < this.images.length; i++) {
      if (i !== index) {
        this.images[i].application = sourceImage.application;
        this.images[i].brand = sourceImage.brand;
        this.images[i].color = sourceImage.color;
      }
    }
  }

  deleteImage(index: number): void {
    this.images.splice(index, 1);
    this.colorOptionsByIndex.splice(index, 1);
    this.imagesUploaded.emit(this.images);
    this.validateChanges.emit(this.isValid());
  }

  isValid(): boolean {
    return (
      this.images.length > 0 &&
      this.images.every(img => img.application && img.brand && img.color) &&
      this.images.some(img => img.isCover)
    );
  }
}
